import { captureException } from '@tink/core/src/sentry';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { OmitStrict } from '@tink/core/src/types';
import { Credentials, CredentialsEntity } from '../../models/credentials';
import { RefreshableItem } from '../../models/refreshableItem';
import { FETCHING_CREDENTIALS_FAILED, getErrorMessageFromStatusCode } from '../../utilities/error';
import {
  authenticateCredentials as authenticateCredentialsImport,
  createCredentials as createCredentialsImport,
  CredentialsRequest,
  getCredentials as getCredentialsImport,
  refreshCredentials as refreshCredentialsImport,
  updateCredentials as updateCredentialsImport,
} from '../../lib-api/services/Credentials';
import { AppDispatch, AppThunk } from '../../stores/redux-store';
import {
  authenticateCredentialsEnd,
  authenticateCredentialsStart,
  createCredentialsFailure,
  createCredentialsStart,
  createCredentialsSuccess,
  getCredentialsStart,
  getCredentialsSuccess,
  refreshCredentialsEnd,
  refreshCredentialsStart,
  setCredentials,
  signExistingCredentialsFailure,
  signExistingCredentialsStart,
  signExistingCredentialsSuccess,
  updateCredentialsFailure,
  updateCredentialsStart,
  updateCredentialsSuccess,
} from '../../stores/credentials-store';
import { setApplicationError } from '../../stores/app-state-store';
import { nestedPopup as globalPopup, StatefulPopup } from '../../utilities/nested-popup';
import { addUserCredentials, setUserCredentials } from '../../stores/user-credentials-store';
import { ProductNames } from '../../models/product';
import {
  CredentialsPoller,
  CredentialsPollerCallback,
} from './credentials-poller/credentials-poller';

export const credentialsPollerGlobal = new CredentialsPoller();

type CreateCredentialsCallbacks = (_: {
  dispatch: AppDispatch;
  shouldClosePopupForAwaitingTpp?: boolean;
  popup?: StatefulPopup;
  poller?: CredentialsPoller;
}) => CredentialsPollerCallback;

export const createCredentialsCallbacks: CreateCredentialsCallbacks = ({
  dispatch,
  popup = globalPopup,
  poller = credentialsPollerGlobal,
}): CredentialsPollerCallback => {
  return {
    onError: () => {
      poller.stop();
    },
    onUpdated: () => {
      poller.stop();
    },
    onAnyUpdate: (credentials: CredentialsEntity) => {
      dispatch(setCredentials(credentials));

      if (
        Credentials.isUpdating(credentials) ||
        Credentials.isUpdated(credentials) ||
        Credentials.hasError(credentials) ||
        Credentials.isAwaitingSupplementalInformation(credentials) ||
        Credentials.isAwaitingThirdPartyAppAuthentication(credentials)
      ) {
        popup.close();
      }
    },
  };
};

export type SignCredentials = (_: {
  credentialsId: string;
  dispatch: AppDispatch;
  poller?: CredentialsPoller;
}) => void;

export const signCredentials: SignCredentials = ({
  credentialsId,
  dispatch,
  poller = credentialsPollerGlobal,
}) => {
  const credentialCallbacks = createCredentialsCallbacks({
    dispatch,
  });

  poller.run(credentialCallbacks, credentialsId);
};

export const signCredentialsWithStatusUpdated = (
  credentialsId: string,
  statusUpdated: number | undefined,
  dispatch: AppDispatch
) => {
  const credentialCallbacks = createCredentialsCallbacks({ dispatch });

  return credentialsPollerGlobal.run(credentialCallbacks, credentialsId, statusUpdated);
};

export const stopPollingCredentials = () => {
  credentialsPollerGlobal.stop();
};

export type CreateCredentialsPayload = {
  credentialsRequest: CredentialsRequest;
  refreshableItems: RefreshableItem[];
  instantSign: boolean;
};

export const createCredentials =
  (
    { credentialsRequest, refreshableItems, instantSign }: CreateCredentialsPayload,
    createCredentialsFn = createCredentialsImport,
    signCredentialsFn = signCredentials
  ): AppThunk =>
  async (dispatch) => {
    dispatch(createCredentialsStart());

    const response = await createCredentialsFn({
      request: credentialsRequest,
      refreshableItems,
    });

    if (response.isOk()) {
      // If user inputs incorrect credentials, we still get OK response but credentials are in AUTHENTICATION_ERROR state
      const createdCredentials: CredentialsEntity = response.data;

      dispatch(createCredentialsSuccess(createdCredentials));
      dispatch(addUserCredentials(createdCredentials));

      if (instantSign) {
        signCredentialsFn({ credentialsId: createdCredentials.id, dispatch });
      }
    } else {
      dispatch(createCredentialsFailure(getErrorMessageFromStatusCode(response.status)));

      dispatch(
        setApplicationError({
          errorStatus: ErrorStatus.INTERNAL_ERROR,
          errorReason: ErrorReason.REQUEST_FAILED_CREATE_CREDENTIALS,
        })
      );
    }
  };

export const updateCredentials =
  (
    credentialsId: string,
    credentialsRequest: CredentialsRequest,
    updateCredentialsFn = updateCredentialsImport,
    signCredentialsFn = signCredentials
  ): AppThunk =>
  async (dispatch) => {
    dispatch(updateCredentialsStart());
    const response = await updateCredentialsFn({
      id: credentialsId,
      request: credentialsRequest,
    });

    if (response.isOk()) {
      const updatedCredentials: CredentialsEntity = response.data;
      dispatch(updateCredentialsSuccess(updatedCredentials));

      signCredentialsFn({ credentialsId: updatedCredentials.id, dispatch });
    } else {
      dispatch(updateCredentialsFailure(getErrorMessageFromStatusCode(response.status)));
    }
  };

export const tokenIsValid = (credentials: CredentialsEntity) => {
  // token is valid for 90 days
  const day = 24 * 60 * 60 * 1000;
  const ninetyDays = 90 * day;
  const now = Date.now();

  return (credentials.statusUpdated || 0) + ninetyDays > now;
};

export type RefreshCredentialsPayload = {
  appUri: string;
  authenticate: boolean;
  credentials: CredentialsEntity;
  refreshableItems: RefreshableItem[];
  productNames: ProductNames[];
  optIn?: boolean;
  callbackUri?: string;
};

export const refreshCredentials =
  (
    {
      credentials,
      authenticate,
      optIn,
      callbackUri,
      refreshableItems,
      appUri,
      productNames,
    }: RefreshCredentialsPayload,
    refreshCredentialsFn = refreshCredentialsImport,
    signCredentialsService = signCredentialsWithStatusUpdated
  ): AppThunk =>
  async (dispatch) => {
    dispatch(refreshCredentialsStart());
    try {
      await refreshCredentialsFn({
        id: credentials.id,
        authenticate,
        optIn,
        request: { appUri, productNames, callbackUri },
        refreshableItems,
      });
    } catch (e) {
      captureException(e, { custom: { message: 'could not refresh credentials' } });
    }
    const { id: credentialsId, statusUpdated } = credentials;

    const refreshedCredentials = await signCredentialsService(
      credentialsId,
      statusUpdated,
      dispatch
    );
    dispatch(refreshCredentialsEnd(refreshedCredentials as CredentialsEntity));
  };

export type RefreshCredentialsByIdPayload = {
  appUri: string;
  authenticate: boolean;
  credentialsId: string;
  refreshableItems: RefreshableItem[];
  productNames: ProductNames[];
  optIn?: boolean;
};

export const refreshCredentialsById =
  (
    {
      credentialsId,
      appUri,
      authenticate,
      optIn,
      refreshableItems,
      productNames,
    }: RefreshCredentialsByIdPayload,
    getCredentialsFn = getCredentialsImport,
    refreshCredentialsFn = refreshCredentialsImport,
    signCredentialsService = signCredentialsWithStatusUpdated
  ): AppThunk =>
  async (dispatch) => {
    dispatch(refreshCredentialsStart());

    const credentialsResponse = await getCredentialsFn(credentialsId, true);
    const credentials = credentialsResponse.data;

    await refreshCredentialsFn({
      id: credentials.id,
      authenticate,
      optIn,
      request: { appUri, productNames },
      refreshableItems,
    });
    const { statusUpdated } = credentials;

    const refreshedCredentials = await signCredentialsService(
      credentialsId,
      statusUpdated,
      dispatch
    );
    dispatch(refreshCredentialsEnd(refreshedCredentials as CredentialsEntity));
  };

type FetchCredentialsPayload = {
  credentialsId: string;
};

export const fetchCredentials =
  ({ credentialsId }: FetchCredentialsPayload, getCredentialsFn = getCredentialsImport): AppThunk =>
  async (dispatch) => {
    dispatch(getCredentialsStart());
    const credentialsResponse = await getCredentialsFn(credentialsId, true);

    if (credentialsResponse.isOk()) {
      const credentials = credentialsResponse.data;
      dispatch(getCredentialsSuccess(credentials));
    } else {
      dispatch(setApplicationError(FETCHING_CREDENTIALS_FAILED));
    }
  };

export const fetchUserCredentials =
  ({ credentialsId }: FetchCredentialsPayload, getCredentialsFn = getCredentialsImport): AppThunk =>
  async (dispatch) => {
    const credentialsResponse = await getCredentialsFn(credentialsId, false);

    if (credentialsResponse.isOk()) {
      dispatch(setUserCredentials(credentialsResponse.data));
    } else {
      dispatch(setApplicationError(FETCHING_CREDENTIALS_FAILED));
    }
  };

export const signExistingCredentials =
  (
    credentialsId: string,
    getCredentialsFn = getCredentialsImport,
    signCredentialsFn = signCredentials
  ): AppThunk =>
  async (dispatch) => {
    dispatch(signExistingCredentialsStart());

    const credentialsResponse = await getCredentialsFn(credentialsId, true);

    if (credentialsResponse.isOk()) {
      const credentials = credentialsResponse.data;

      dispatch(signExistingCredentialsSuccess(credentials));

      signCredentialsFn({ credentialsId: credentials.id, dispatch });
    } else {
      dispatch(
        signExistingCredentialsFailure(getErrorMessageFromStatusCode(credentialsResponse.status))
      );
    }
  };

type AuthenticateCredentialsByIdPayload = {
  credentialsId: string;
  appUri: string;
  productNames: ProductNames[];
};
export const authenticateCredentialsById =
  (
    { credentialsId, appUri, productNames }: AuthenticateCredentialsByIdPayload,
    getCredentialsFn = getCredentialsImport,
    authenticateCredentialsFn = authenticateCredentialsImport,
    signCredentialsFn = signCredentials
  ): AppThunk =>
  async (dispatch) => {
    dispatch(authenticateCredentialsStart());

    const authenticateResponse = await authenticateCredentialsFn({
      id: credentialsId,
      request: { appUri, productNames },
    });

    if (authenticateResponse.isOk()) {
      signCredentialsFn({ credentialsId, dispatch });
    } else {
      dispatch(
        updateCredentialsFailure(getErrorMessageFromStatusCode(authenticateResponse.status))
      );
    }

    const credentialsResponse = await getCredentialsFn(credentialsId, false);
    dispatch(authenticateCredentialsEnd(credentialsResponse.data));
  };

export const containsCredentials = (
  credentialsHaystack: CredentialsEntity[],
  credentialsNeedle: OmitStrict<CredentialsRequest, 'productNames'>
): CredentialsEntity | undefined => {
  return credentialsHaystack.find((credentials) => {
    if (credentials.providerName !== credentialsNeedle.providerName) {
      return false;
    }

    if (!credentialsNeedle.fields) {
      return true;
    }

    const fieldsMatching = Object.keys(credentials.fields).filter((field) => {
      return credentials.fields[field] === credentialsNeedle.fields[field];
    });

    return fieldsMatching.length === Object.keys(credentials.fields).length;
  });
};
